import {OctTable} from "@oceantech/oceantech-ui";
import {FC, useContext, useEffect, useMemo, useState} from "react";
import {
  CODE_SUCCESS,
  INIT_SEARCH_OBJECT,
} from "../utils/Constant";
import { IConfigTable } from "../models/OctResponse";
import { Button, InputGroup } from "react-bootstrap";
import BuildPlanModal from "./modals/BuildPlanModal";
import TextValidator from "../component/TextValidator";
import { deletePlan, getPlans } from "./services/PlanServices";
import { IPlan, IPlanSearch } from "./models/PlanModel";
import { AppContext } from "../appContext/AppContext";
import { ConfirmDialog } from "../component/ConfirmDialog";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import BuildPlansColumns from "./columns";
import {formatDateDTO} from "../utils/FormatUtils";
import LabelRequired from "../component/LabelRequired";
import DatePickerCustom from "../component/date-picker/DatePickerCustom";
import {generateForm, generateInitValues} from "../utils/AppFunction";
import {PLAN_MODEL_JSON} from "./constants/buildPlans";
import {BuildPlanProvider} from "./constants/context";

export const BuildPlans: FC = () => {
  const { setIsLoading } = useContext(AppContext);
  const [objectSearch, setObjectSearch] = useState<IPlanSearch>(INIT_SEARCH_OBJECT);
  const [configTable, setConfigTable] = useState<IConfigTable>({
    totalPages: 1,
    totalElements: 5,
    numberOfElements: 5,
  });
  const [showModal, setShowModal] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [plan, setPlan] = useState<any>(null);
  const [reload, setReload] = useState(false);
  const [listJsonGenerate, setListJsonGenerate] = useState<any>([]);
  const [showModalConfirm, setShowModalConfirm] = useState({
    open: false,
    message: "",
    type: "",
  });

  const contextProps = useMemo(() => ({
    setPlan,
    plan: plan as IPlan,
  }), [plan])

  useEffect(() => {
    handleGetPlans();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, objectSearch.pageIndex, objectSearch.pageSize]);

  useEffect(() => {
    updateForm();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateForm = async () => {
    const data = await generateForm(PLAN_MODEL_JSON.BUILD_PLAN, setIsLoading);
    setPlan({
      ...generateInitValues(data),
      doCreation: new Date(),
      // creator: dataUser?.sub,
    });
    setListJsonGenerate(data || []);
  };

  const handleChange = (name: string, value: any) => {
    setObjectSearch({
      ...objectSearch,
      [name]: value,
    });
  };

  const handleSubmitSearch = () => {
    setReload(!reload);
    setObjectSearch({
      ...objectSearch,
      pageIndex: 1,
    });
  };

  const handleGetPlans = async () => {
    try {
      setIsLoading(true);
      const params = {
        ...objectSearch,
        startDate: objectSearch?.startDate ? formatDateDTO(objectSearch?.startDate) : null,
        endDate: objectSearch?.endDate ? formatDateDTO(objectSearch?.endDate) : null,
      };
      const { data } = await getPlans(params);
      if (data?.code === CODE_SUCCESS) {
        setData(data?.data?.content);
        setConfigTable({
          totalPages: data?.data?.totalPages,
          totalElements: data?.data?.totalElements,
          numberOfElements: data?.data?.numberOfElements,
        });
      }
    } catch (error) {
      console.error("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenPlanModal = (plan: IPlan | null) => {
    setShowModal(true);
    setPlan((preState: any) => ({...preState, ...plan}));
  };

  const handleClosePlanModal = () => {
    setShowModal(false);
    setPlan(null);
    setReload(!reload);
  };

  const handleOpenConfirmDialog = (
    message: string,
    plan: IPlan | null,
    type: string
  ) => {
    setShowModalConfirm({ open: true, message, type });
    setPlan(plan);
  };

  const handleCloseConfirmDialog = () => {
    setShowModalConfirm({ open: false, message: "", type: "" });
    setPlan(null);
  };

  const handleConfirm = () => {
    const options: { [key: string]: () => Promise<void> } = {
      delete: () => handleDeletePlan(plan?.id ?? ""),
    };
    options[showModalConfirm?.type]?.();
  };

  const handleDeletePlan = async (id: string) => {
    setIsLoading(true);
    try {
      const { data } = await deletePlan(id);
      if (data?.code === CODE_SUCCESS) {
        toast.success("Xóa thành công");
        handleCloseConfirmDialog();
        setReload(!reload);
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      toast.error(
        axiosError.response?.data?.message ?? "Có lỗi xảy ra, vui lòng thử lại!"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const columns = useMemo(() => BuildPlansColumns({
    handleOpenPlanModal,
    handleOpenConfirmDialog
  }), []);

  return (
    <BuildPlanProvider value={contextProps}>
      <div className="pe-list">
        <div className="h-100 bg-white">
          <div className="reception-list__container spaces py-0">
            <div className="text-title border-bottom px-3 pt-2">
              Danh sách kế hoạch
            </div>

            <div className="d-flex spaces pr-8 mt-6 bg-white justify-content-between align-items-end mb-6">
              <div className=" bg-white mr-13 spaces">
                <Button
                  className="btn-fill ml-10 spaces min-w-90px btn btn-primary"
                  onClick={() => handleOpenPlanModal(null)}
                >
                  <i className="bi bi-plus-lg fs-3"></i>
                  Thêm mới
                </Button>
              </div>
              <div className="d-flex spaces mx-10 align-items-end">
                <LabelRequired className="max-content-width spaces mr-5" label="Từ ngày"/>
                <DatePickerCustom
                  placeholderText="dd/mm/yyyy"
                  name="startDate"
                  value={objectSearch?.startDate ?? ""}
                  setDateValue={(date) => {
                    handleChange("startDate", date);
                  }}
                  dateFormatOutput="YYYY-MM-DD"
                />
              </div>
              <div className="d-flex spaces mx-10 align-items-end">
                <LabelRequired className="max-content-width spaces mr-5" label="Đến ngày"/>
                <DatePickerCustom
                  placeholderText="dd/mm/yyyy"
                  name="endDate"
                  value={objectSearch?.endDate ?? ""}
                  setDateValue={(date) => {
                    handleChange("endDate", date);
                  }}
                  dateFormatOutput="YYYY-MM-DD"
                />
              </div>
              <div className="text-lable-input mb-0 spaces spaces mt-5 mr-5 form-label max-content-width">Từ khóa</div>
              <div className="flex-grow-1">
                <InputGroup className="spaces h-29 px-0">
                  <TextValidator
                    className="spaces h-29 min-w-200 rounded-end-0"
                    name="keyword"
                    placeholder="Tìm kiếm theo mã, tên kế hoạch"
                    value={objectSearch?.keyword || ""}
                    onChange={(e: any) =>
                      handleChange("keyword", e.target.value)
                    }
                  />
                  <Button
                    className="btn-fill spaces h-29"
                    onClick={handleSubmitSearch}
                  >
                    <i className="bi bi-search"></i>
                    <span>Lọc</span>
                  </Button>
                </InputGroup>
              </div>
            </div>

            <OctTable
              id="searchPlan"
              className={`mt-7 table-background-th width-100 spaces h-calc-vh-230`}
              data={data}
              columns={columns}
              searchObject={objectSearch}
              setSearchObject={setObjectSearch}
              noToolbar={true}
              totalPages={configTable.totalPages}
              totalElements={configTable.totalElements}
              numberOfElements={configTable.numberOfElements}
              isShowSelection={false}
              clearToolbar={true}
            />
          </div>
        </div>

        {showModal && (
          <BuildPlanModal
            show={showModal}
            onCloseDialog={handleClosePlanModal}
            formJson={listJsonGenerate}
          />
        )}

        {showModalConfirm.open && (
          <ConfirmDialog
            show={showModalConfirm.open}
            message={showModalConfirm.message}
            yes="Xác nhận"
            onYesClick={handleConfirm}
            cancel="Hủy"
            onCancelClick={handleCloseConfirmDialog}
          />
        )}
      </div>
    </BuildPlanProvider>
  );
};

export default BuildPlans;
